import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  FileText, ArrowLeft, Edit, Save, Eye, Code, Lock, Building, 
  Loader2, Download, Copy, Check
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper to render template content as formatted HTML
const renderTemplateContent = (content) => {
  if (!content) return '';
  
  // If content looks like HTML, render it
  if (content.includes('<') && content.includes('>')) {
    return content;
  }
  
  // Convert plain text with line breaks to HTML
  let html = content
    .replace(/\n\n/g, '</p><p>')
    .replace(/\n/g, '<br/>')
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/__(.*?)__/g, '<strong>$1</strong>')
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    .replace(/_(.*?)_/g, '<em>$1</em>');
  
  // Highlight template variables
  html = html.replace(/\{\{(\w+)\}\}/g, '<span class="bg-yellow-100 text-yellow-800 px-1 rounded font-mono text-sm">{{$1}}</span>');
  
  return `<p>${html}</p>`;
};

const ContractTemplateViewPage = () => {
  const { templateId } = useParams();
  const navigate = useNavigate();
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  const [viewMode, setViewMode] = useState('preview'); // 'preview' or 'source'
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchTemplate();
  }, [templateId]);

  const fetchTemplate = async () => {
    const token = localStorage.getItem('token');
    setLoading(true);
    try {
      const response = await axios.get(`${API}/contract-templates/${templateId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTemplate(response.data);
      setEditData({
        name: response.data.name || '',
        description: response.data.description || '',
        type: response.data.type || 'time_material',
        content_template: response.data.content_template || '',
        visibility: response.data.visibility || 'personal'
      });
    } catch (error) {
      toast.error('Failed to load template');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveChanges = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/contract-templates/${templateId}`,
        editData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Template updated successfully');
      setShowEditModal(false);
      fetchTemplate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update template');
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const handleCopyContent = () => {
    navigator.clipboard.writeText(template?.content_template || '');
    setCopied(true);
    toast.success('Content copied to clipboard');
    setTimeout(() => setCopied(false), 2000);
  };

  const getTypeBadge = (type) => {
    const types = {
      'nda': { label: 'NDA', color: 'bg-orange-100 text-orange-800' },
      'non_compete': { label: 'Non-Compete', color: 'bg-red-100 text-red-800' },
      'time_material': { label: 'Time & Material', color: 'bg-green-100 text-green-800' },
      'regie': { label: 'Time & Material', color: 'bg-green-100 text-green-800' },
      'fixed_price': { label: 'Fixed Price', color: 'bg-blue-100 text-blue-800' },
      'project_based': { label: 'Fixed Price', color: 'bg-blue-100 text-blue-800' },
      'freelance': { label: 'Freelance', color: 'bg-purple-100 text-purple-800' }
    };
    const t = types[type] || { label: type, color: 'bg-gray-100 text-gray-800' };
    return <Badge className={t.color}>{t.label}</Badge>;
  };

  if (loading) {
    return (
      <div className="p-8 flex justify-center items-center h-96">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  if (!template) {
    return (
      <div className="p-8 text-center">
        <p className="text-gray-500">Template not found</p>
        <Button variant="outline" className="mt-4" onClick={() => navigate('/my-contract-templates')}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Templates
        </Button>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <Button variant="ghost" onClick={() => navigate('/my-contract-templates')} className="mb-4">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Templates
        </Button>
        
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-2xl font-bold" style={{ color: '#1B3240' }}>{template.name}</h1>
            <p className="text-gray-600 mt-1">{template.description || 'No description'}</p>
            <div className="flex gap-2 mt-3">
              {getTypeBadge(template.type)}
              {template.is_predefined ? (
                <Badge className="bg-blue-100 text-blue-800">Snow Exchange</Badge>
              ) : template.visibility === 'company' ? (
                <Badge className="bg-purple-100 text-purple-800"><Building className="w-3 h-3 mr-1" />Company</Badge>
              ) : (
                <Badge className="bg-gray-100 text-gray-800"><Lock className="w-3 h-3 mr-1" />Personal</Badge>
              )}
            </div>
          </div>
          
          <div className="flex gap-2">
            {!template.is_predefined && (
              <Button variant="outline" onClick={() => setShowEditModal(true)}>
                <Edit className="w-4 h-4 mr-2" />
                Edit
              </Button>
            )}
            <Button variant="outline" onClick={handleCopyContent}>
              {copied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
              {copied ? 'Copied!' : 'Copy'}
            </Button>
          </div>
        </div>
      </div>

      {/* View Mode Toggle */}
      <Card className="mb-4">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex gap-2">
              <Button 
                variant={viewMode === 'preview' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setViewMode('preview')}
                style={viewMode === 'preview' ? { backgroundColor: '#62D84E' } : {}}
              >
                <Eye className="w-4 h-4 mr-2" />
                Preview
              </Button>
              <Button 
                variant={viewMode === 'source' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setViewMode('source')}
                style={viewMode === 'source' ? { backgroundColor: '#1B3240' } : {}}
              >
                <Code className="w-4 h-4 mr-2" />
                Source Code
              </Button>
            </div>
            <p className="text-sm text-gray-500">
              Variables like <code className="bg-yellow-100 px-1 rounded">{"{{resource_name}}"}</code> will be replaced with actual data
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Content Display */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <FileText className="w-5 h-5 mr-2" style={{ color: '#62D84E' }} />
            Contract Content
          </CardTitle>
        </CardHeader>
        <CardContent>
          {viewMode === 'preview' ? (
            <div 
              className="prose prose-sm max-w-none p-6 bg-white border rounded-lg contract-preview"
              style={{ 
                lineHeight: '1.8',
                fontFamily: 'Georgia, serif'
              }}
              dangerouslySetInnerHTML={{ 
                __html: renderTemplateContent(template.content_template) 
              }}
            />
          ) : (
            <pre className="p-4 bg-gray-900 text-green-400 rounded-lg overflow-x-auto text-sm font-mono">
              {template.content_template}
            </pre>
          )}
          
          {/* Templates Disclaimer */}
          <p className="text-xs text-gray-500 mt-4 pt-3 border-t">
            Templates are provided for convenience only and must be independently reviewed.
          </p>
        </CardContent>
      </Card>

      {/* Edit Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Template</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Template Name</Label>
                <Input
                  className="mt-1"
                  value={editData.name}
                  onChange={(e) => setEditData({...editData, name: e.target.value})}
                />
              </div>
              <div>
                <Label>Contract Type</Label>
                <Select 
                  value={editData.type} 
                  onValueChange={(v) => setEditData({...editData, type: v})}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="nda">NDA</SelectItem>
                    <SelectItem value="non_compete">Non-Compete</SelectItem>
                    <SelectItem value="time_material">Time & Material</SelectItem>
                    <SelectItem value="fixed_price">Fixed Price</SelectItem>
                    <SelectItem value="freelance">Freelance</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Description</Label>
              <Input
                className="mt-1"
                value={editData.description}
                onChange={(e) => setEditData({...editData, description: e.target.value})}
              />
            </div>

            <div>
              <Label>Visibility</Label>
              <Select 
                value={editData.visibility} 
                onValueChange={(v) => setEditData({...editData, visibility: v})}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="personal">Personal (Only you)</SelectItem>
                  <SelectItem value="company">Company (Team members)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Template Content</Label>
              <Textarea
                className="mt-1 min-h-[300px] font-mono text-sm"
                value={editData.content_template}
                onChange={(e) => setEditData({...editData, content_template: e.target.value})}
              />
              <p className="text-xs text-gray-500 mt-1">
                Use {"{{variable_name}}"} for dynamic fields. Available: resource_name, daily_rate, pm_company_name, rm_company_name, etc.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditModal(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSaveChanges} 
              disabled={saving}
              style={{ backgroundColor: '#62D84E' }}
            >
              {saving ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* CSS for preview */}
      <style>{`
        .contract-preview h1, .contract-preview h2, .contract-preview h3 {
          color: #1B3240;
          margin-top: 1.5rem;
          margin-bottom: 0.75rem;
        }
        .contract-preview h1 { font-size: 1.5rem; font-weight: bold; }
        .contract-preview h2 { font-size: 1.25rem; font-weight: bold; }
        .contract-preview h3 { font-size: 1.1rem; font-weight: 600; }
        .contract-preview p { margin-bottom: 1rem; text-align: justify; }
        .contract-preview ul, .contract-preview ol { margin-left: 1.5rem; margin-bottom: 1rem; }
        .contract-preview li { margin-bottom: 0.5rem; }
        .contract-preview strong { color: #1B3240; }
      `}</style>
    </div>
  );
};

export default ContractTemplateViewPage;
